import React from 'react';
import { useTranslation } from '@ikea-customer-self-serve/gatsby-plugin-translations';
import Loading, { LoadingBall } from '@ingka/loading';
import Button, { ButtonType } from '@ingka/button';
import plusIcon from '@ingka/ssr-icon/paths/plus-small';
import QuantityDropdown from '@ingka/quantity-dropdown';
import Status from '@ingka/status';
import styled from 'styled-components';
import { useCartItemQuantityController } from '../hooks/useOrder';
import { useFindEquivalentSpareParts } from '../hooks/useFindEquivalentSpareParts';
import { Item, ItemProps } from './Item';
import { useCart, calculateNewNumberOfItems } from '../contexts/Cart';
import TMarkUp from './TMarkUp';

/* globals CSS_CONFIG */

const AddQuantityDropdown = styled(QuantityDropdown)`
  margin-inline-end: 20px;
`;

interface FindEquivalentSparePartsProps {
  id: string;
}

interface AddToCartTextButtonProps {
  loading: boolean;
  addToCart: () => void;
  maxNumberToSelect: number;
}

const FindEquivalentSpareParts: React.FC<FindEquivalentSparePartsProps> = ({
  id,
}): JSX.Element => {
  const { t } = useTranslation();
  const [items, isLoading, isError] = useFindEquivalentSpareParts(id);
  if (isLoading) {
    return (
      <Loading
        text={t('loading', {
          loading: t('spareParts.search.equivalentSpareParts'),
        })}
      >
        <LoadingBall />
      </Loading>
    );
  }

  if (isError) {
    return (
      <div>
        <TMarkUp value={t(`spareParts.search.noReplacementFound`)} />
      </div>
    );
  }
  const addDisplayItems = items.map(
    (item, index): JSX.Element => {
      return <AddItemDisplay key={index} item={item} />;
    }
  );
  return (
    <div>
      <h4 className="h4">
        <TMarkUp value={t(`spareParts.search.equivalentSpareParts`)} />
      </h4>
      <p>
        <TMarkUp value={t(`spareParts.search.guaranteedEquivalent`)} />
      </p>
      <hr />
      {items.length > 0 && <>{addDisplayItems}</>}
    </div>
  );
};
const StyledStatus = styled(Status)`
  margin-inline-end: 8px;
`;
interface ItemWithStatusProps extends ItemProps {
  status: string;
}
export const ErrorStatus: React.FC<ItemWithStatusProps> = ({
  item,
  status,
}): JSX.Element => (
  <Item item={item}>
    <StyledStatus statusColor="red" />
    <div>
      <TMarkUp value={status} />
    </div>
  </Item>
);

const AddToCartButton: React.FC<AddToCartTextButtonProps> = ({
  loading,
  addToCart,
  maxNumberToSelect,
}): JSX.Element => {
  const { t } = useTranslation();
  const buttonProps = {
    type: 'primary' as ButtonType,
    small: true,
    loading,
    onClick: addToCart,
    disabled: !maxNumberToSelect,
    'aria-label': t(`spareParts.ariaLabel.addToCart`),
  };
  if (CSS_CONFIG.ADDTOCARTTEXTVISIBLE) {
    return (
      <Button
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...buttonProps}
        text={t(`spareParts.ariaLabel.addToCart`)}
      />
    );
  }
  return (
    <Button
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buttonProps}
      ssrIcon={plusIcon}
      iconOnly
    />
  );
};
export const EquivalentItemDisplay: React.FC<ItemWithStatusProps> = ({
  item,
  status,
}): JSX.Element => {
  return (
    <>
      <ErrorStatus item={item} status={status} />
      <FindEquivalentSpareParts id={item.replacementId} />
    </>
  );
};

export const AddItemDisplay: React.FC<ItemProps> = ({ item }): JSX.Element => {
  const [
    { loading, quantity },
    { onQuantityChange, addToCart },
  ] = useCartItemQuantityController(item);
  const { t } = useTranslation();
  const CartContext = useCart();
  const maxNumberToSelect = calculateNewNumberOfItems(
    CartContext.getTotalNrOfItems(),
    item.stock,
    Math.min(item.stock, CSS_CONFIG.ITEM_LIMIT)
  );
  return (
    <Item item={item}>
      <AddQuantityDropdown
        max={maxNumberToSelect}
        onChange={onQuantityChange}
        disabled={!maxNumberToSelect}
        value={Math.min(quantity, maxNumberToSelect)}
        ariaLabel={t('spareParts.ariaLabel.changeQuantity')}
      />
      <AddToCartButton
        loading={loading}
        addToCart={addToCart}
        maxNumberToSelect={maxNumberToSelect}
      />
    </Item>
  );
};

export const ShowStatusItem: React.FC<ItemProps> = ({ item }): JSX.Element => {
  const { t } = useTranslation();

  switch (item.status) {
    case 'STOCK_NOT_RECEIVED':
    case 'ORDER_AT_CSC':
      return (
        <ErrorStatus item={item} status={t('spareParts.status.orderAtCSC')} />
      );
    case 'NOT_AVAILABLE':
      return (
        <ErrorStatus item={item} status={t(`spareParts.status.notAvailable`)} />
      );
    case 'DISCONTINUED_WITH_REPLACEMENTID':
      return (
        <EquivalentItemDisplay
          item={item}
          status={t('spareParts.status.discontinued')}
        />
      );
    case 'OUT_OF_STOCK':
      return (
        <EquivalentItemDisplay
          item={item}
          status={t('spareParts.status.outOfStock')}
        />
      );
    default:
      return <AddItemDisplay item={item} />;
  }
};

export const SelectedItem: React.FC<ItemProps> = ({
  item,
}): JSX.Element | null => {
  if (!item) {
    return null;
  }
  return <ShowStatusItem item={item} />;
};
